import {
  commonChartConfig,
  chartLegendConfig,
  groupOtherData,
  getTooltipPositionFn,
} from '../common-chart-config';
import * as echarts from 'echarts';
import {
  DEFAULT_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS,
  OTHER_SEGMENT_NAME,
} from '../../consts';
import { isNull } from 'lodash';
import { APIPieChartAttributes, HexColor, Maybe } from '@ardoq/api-types';
import { formatNumber, Locale } from '@ardoq/locale';
import {
  Chart,
  ContainerSize,
  PieChartClickHandler,
} from '../categoricalCharts/EChart';
import { type I18nLocale, t } from '@ardoq/i18n';

// Echarts renders to canvas, which doesn't support CSS variables.
const textModerateColor = '#55575d';

type PieChartDataSegments = {
  count: number;
  chartData: Array<{ name: string; value: number; isOtherSegment: boolean }>;
  isOtherSeries: (index: number) => boolean;
  otherDataSegments: Array<{ name: string; value: number }>;
};

export const getPieChartDataSegments = (
  data: APIPieChartAttributes['data'] | null,
  numberOfSegments = DEFAULT_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS
): PieChartDataSegments => {
  const dataArray = isNull(data)
    ? []
    : (data as Array<{ label: string; value: number }>)
        .map(dataPoint => ({
          name: dataPoint.label,
          value: dataPoint.value,
          isOtherSegment: false,
        }))
        .filter(({ name }) => name !== '_time');

  const count = dataArray.reduce((memo, { value }) => memo + value, 0);

  const [visibleDataSegments, otherDataSegments, isOtherSeries] =
    groupOtherData(dataArray, numberOfSegments);
  const otherDataValue = otherDataSegments.reduce(
    (memo, { value }) => memo + value,
    0
  );

  const chartData = visibleDataSegments.concat(
    otherDataValue
      ? {
          name: OTHER_SEGMENT_NAME,
          value: otherDataValue,
          isOtherSegment: true,
        }
      : []
  );
  return { chartData, isOtherSeries, otherDataSegments, count };
};

const getFormattedPercentage = (
  percent: number,
  shouldUseDecimalPoint: boolean,
  shouldUseApproximationSymbol: boolean
) =>
  `${shouldUseApproximationSymbol ? '～' : ''}${
    shouldUseDecimalPoint ? percent.toFixed(1) : Math.round(percent)
  }%`;

// Legend percentages omit the ～ approximation symbol for cleaner display
const getLegendPercentage = (percent: number, shouldUseDecimalPoint: boolean) =>
  `${shouldUseDecimalPoint ? percent.toFixed(1) : Math.round(percent)}%`;

type PercentageFlags = {
  shouldUseDecimalPoint: boolean;
  shouldUseApproximationSymbol: boolean;
};

// Determines whether percentages need decimals and/or approximation symbols.
// Uses decimals when rounded percentages don't sum to 100%.
// Uses ～ when even single-decimal percentages don't sum to 100%.
const getPercentageFlags = (percentages: number[]): PercentageFlags => {
  const shouldUseDecimalPoint =
    percentages.reduce((sum, p) => sum + Math.round(p), 0) !== 100;
  const shouldUseApproximationSymbol =
    shouldUseDecimalPoint &&
    percentages.reduce((sum, p) => sum + Math.round(p * 10), 0) !== 1000;
  return { shouldUseDecimalPoint, shouldUseApproximationSymbol };
};

type GetTooltipConfig = {
  count: number;
  otherDataSegments: Array<{ name: string; value: number }>;
  isOtherSeries: (index: number) => boolean;
  shouldUseDecimalPoint: boolean;
  shouldUseApproximationSymbol: boolean;
  myFormatNumber: (number: number) => string;
  dashboardContainerRef: HTMLDivElement | null;
};

const getTooltipConfig = ({
  count,
  otherDataSegments,
  isOtherSeries,
  shouldUseDecimalPoint,
  shouldUseApproximationSymbol,
  myFormatNumber,
  dashboardContainerRef,
}: GetTooltipConfig): echarts.EChartsOption['tooltip'] => ({
  extraCssText: `
        max-width: 240px;
        white-space: normal;
      `,
  trigger: 'item',
  confine: false,
  position: getTooltipPositionFn(dashboardContainerRef),
  formatter: ({ marker, name, value, percent, dataIndex }: any) =>
    `${name}<br />${marker}<small> <b>${getFormattedPercentage(
      percent,
      shouldUseDecimalPoint,
      shouldUseApproximationSymbol
    )}</b> (${myFormatNumber(value)} out of ${myFormatNumber(count)})</small>${
      isOtherSeries(dataIndex)
        ? `<br /><small>${otherDataSegments
            .slice(0, 10)
            .map(({ name, value }) => `<b>${myFormatNumber(value)}</b> ${name}`)
            .join('<br />')}${
            otherDataSegments.length < 10
              ? ''
              : `<br /><i>... ${otherDataSegments.length - 10} more items</i>`
          }</small>`
        : ''
    }`,
});

const addColorsToChartData = (
  chartData: { name: string; value: number; isOtherSegment: boolean },
  colors: Record<string, HexColor>
) => ({
  ...chartData,
  itemStyle: { color: colors[chartData.name] },
});

// Because of Gremlin, there are some weird cases where name can be a number, so convert to string first to be safe.
const toLegendName = (name: string): string =>
  typeof name === 'string' ? name : String(name);

// Approximate character width (px) at fontSize 12 for name column sizing.
const APPROX_CHAR_WIDTH = 8;
// Minimum characters to show in the name column before truncation.
const MIN_NAME_CHARS = 5;

const captionTextStyle = {
  fontSize: 12,
  lineHeight: 16,
  color: textModerateColor,
};

// Compute the largest pie radius (px) that fits within the left 55% area
// given the pie center at (28%, 50%).
const computePieRadius = (width: number, height: number): number =>
  Math.floor(Math.min(width * 0.27, height * 0.5) * 0.85);

// --- Improved chart options (pie on left, vertical legend on right) ---
//
// Layout: The pie is centered in the left portion using percentage-based
// positioning. The legend occupies the right portion with echarts' built-in
// scroll support. Legend height and name truncation are updated responsively
// via getResizeOptions — a partial setOption applied directly on the echarts
// instance during resize, bypassing React to preserve interaction state.

const getPieChartOptions = (
  { count, otherDataSegments, isOtherSeries, chartData }: PieChartDataSegments,
  orderedLegendNames: string[],
  hasClickHandler: boolean,
  colors: Record<string, HexColor>,
  showTotal: boolean,
  numberFormatOptions: string | undefined,
  dashboardContainerRef: HTMLDivElement | null,
  userLocale: Locale,
  i18nLocale: I18nLocale,
  isAbbreviated: boolean
): echarts.EChartsOption => {
  const myFormatNumber = (number: number) =>
    formatNumber(number, numberFormatOptions, userLocale, isAbbreviated);
  const formattedTotal = myFormatNumber(count);

  const chartDataWithColor = chartData.map(data =>
    addColorsToChartData(data, colors)
  );

  const percentages = chartDataWithColor.map(
    ({ value }) => (100 * value) / count
  );
  const { shouldUseDecimalPoint, shouldUseApproximationSymbol } =
    getPercentageFlags(percentages);

  // formatter and textStyle.rich.name.width are intentionally omitted here.
  // They are set exclusively by getResizeOptionsCallback on each resize,
  // which computes truncation and column width from actual pixel dimensions.
  // Including them here would cause a race: React's setOption could overwrite
  // the resize callback's values with stale/untruncated ones.
  const legendConfig = {
    ...chartLegendConfig,
    type: 'scroll' as const,
    orient: 'vertical' as const,
    icon: 'circle',
    itemWidth: 10, // color swatch diameter (px)
    itemHeight: 10, // color swatch diameter (px)
    left: '60%', // legend starts at 60% of container width (pie occupies left portion)
    right: 8, // gap between legend and right edge (px)
    top: 'middle', // vertically center the legend
    padding: 5, // internal legend box padding (px)
    pageIcons: {
      vertical: [
        // chevron up
        'path://M14.4086 12.4234C14.7336 12.0984 14.7336 11.5734 14.4086 11.2484L10.5836 7.42344C10.2586 7.09844 9.73359 7.09844 9.40859 7.42344L5.58359 11.2484C5.25859 11.5734 5.25859 12.0984 5.58359 12.4234C5.90859 12.7484 6.43359 12.7484 6.75859 12.4234L10.0003 9.1901L13.2336 12.4234C13.5586 12.7401 14.0919 12.7401 14.4086 12.4234Z',
        // chevron down
        'path://M5.59246 7.75937C5.91746 7.43438 6.44246 7.43438 6.76746 7.75937L10.0008 10.9927L13.2341 7.75937C13.5591 7.43438 14.0841 7.43438 14.4091 7.75937C14.7341 8.08437 14.7341 8.60937 14.4091 8.93437L10.5841 12.7594C10.2591 13.0844 9.73412 13.0844 9.40912 12.7594L5.58412 8.93437C5.26746 8.61771 5.26746 8.08437 5.59246 7.75937Z',
      ],
    },
    data: orderedLegendNames,
    textStyle: captionTextStyle,
    tooltip: { show: true },
  };

  return {
    ...commonChartConfig,
    aria: {
      ...commonChartConfig.aria,
      label: {
        description: getPieChartAriaDescription({
          chartData,
          count,
          formattedTotal,
          myFormatNumber,
          shouldUseDecimalPoint,
          i18nLocale,
        }),
      },
    },
    padding: 16,
    tooltip: getTooltipConfig({
      count,
      otherDataSegments,
      isOtherSeries,
      shouldUseDecimalPoint,
      shouldUseApproximationSymbol,
      myFormatNumber,
      dashboardContainerRef,
    }),
    series: [
      {
        type: 'pie',
        radius: showTotal ? ['42%', '85%'] : '85%',
        center: ['33%', '50%'], // horizontally centered in the left portion, before the legend
        data: chartDataWithColor,
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
        },
        ...(!hasClickHandler ? { cursor: 'default' } : {}),
        label: { show: false },
        labelLine: { show: false },
      },
    ],
    legend: legendConfig,
    // fontSize is intentionally omitted from textStyle/subtextStyle here.
    // It is set exclusively by getResizeOptionsCallback on each resize,
    // which computes font size from actual pixel dimensions. Including it
    // here would cause flicker: React's setOption would reset to a static
    // value before the resize callback applies the computed one.
    title: showTotal
      ? {
          show: true,
          text: t(i18nLocale, 'dashboard:pieChart.total', 'Total'),
          subtext: formattedTotal,
          left: '33%',
          top: '47%',
          textAlign: 'center',
          textVerticalAlign: 'middle',
          textStyle: {
            fontWeight: 'normal',
            color: textModerateColor,
          },
          subtextStyle: {
            fontWeight: 'bold',
            color: textModerateColor,
          },
          itemGap: 4,
          padding: 0,
        }
      : { show: false, text: '', subtext: '' },
    height: '100%',
  };
};

export type PieChartWidgetProps = {
  data: Array<{ label: string; value: number; id?: string }>;
  colors: Record<string, string>;
  getClickHandler?: (
    otherDataSegments: Array<{ name: string }>
  ) => PieChartClickHandler;
  userLocale: Locale;
  showTotal: boolean;
  numberFormatOptions?: string;
  numberSlices?: number;
  i18nLocale: I18nLocale;
  isAbbreviated?: boolean;
};

const getResizeOptionsCallback =
  (pieChartDataSegments: PieChartDataSegments, showTotal: boolean) =>
  ({ width, height }: ContainerSize) => {
    const totalMargin = 32;
    const { chartData: improvedChartData, count } = pieChartDataSegments;
    const percentages = improvedChartData.map(
      ({ value }) => (100 * value) / count
    );
    const { shouldUseDecimalPoint } = getPercentageFlags(percentages);
    const percentageByName = Object.fromEntries(
      improvedChartData.map(({ name, value }) => [
        name,
        getLegendPercentage((100 * value) / count, shouldUseDecimalPoint),
      ])
    );
    // Legend occupies 40% of container width (100% - 60% left offset).
    // Subtract: right padding (8) + legend padding (5*2) + swatch+gap (20) + percent column (~48) = 86
    const availableNameWidth = width * 0.4 - 86;
    const maxChars = Math.max(
      Math.floor(availableNameWidth / APPROX_CHAR_WIDTH),
      MIN_NAME_CHARS
    );
    const truncate = (name: string): string =>
      name.length > maxChars ? `${name.slice(0, maxChars - 1)}...` : name;

    const longestTruncated = Math.max(
      ...improvedChartData.map(
        ({ name }) =>
          // There's a bug caused by gremlin returning numbers for name instead of string. Cast to string to be safe.
          truncate(typeof name === 'string' ? name : String(name)).length
      )
    );
    const outerRadius = computePieRadius(width, height);
    // 0.49 ≈ 42/85, preserving the ratio between the initial inner ('42%')
    // and outer ('85%') radius percentages from the series config.
    const innerRadiusPx = showTotal ? Math.floor(outerRadius * 0.49) : 0;
    const valueFontSize = Math.max(
      Math.min(Math.floor(innerRadiusPx / 4), 16),
      12
    );

    return {
      series: [
        {
          radius: showTotal ? [innerRadiusPx, outerRadius] : outerRadius,
        },
      ],
      legend: {
        height: Math.max(height - totalMargin, 0),
        formatter: (name: string) => {
          const percent = percentageByName[name] ?? '';
          return `{name|${truncate(name)}} {percent|${percent}}`;
        },
        textStyle: {
          rich: {
            name: {
              width: longestTruncated * APPROX_CHAR_WIDTH,
            },
          },
        },
      },
      ...(showTotal
        ? {
            title: {
              textStyle: { fontSize: valueFontSize },
              subtextStyle: { fontSize: valueFontSize },
            },
          }
        : {}),
    };
  };

const PieChartWidget = ({
  data,
  colors,
  dashboardContainerRef,
  userLocale,
  getClickHandler,
  numberFormatOptions = 'default',
  numberSlices = DEFAULT_NUMBER_OF_SEGMENTS_FOR_PIE_CHARTS_AND_STACKED_BAR_CHARTS,
  showTotal,
  i18nLocale,
  isAbbreviated = false,
}: PieChartWidgetProps & { dashboardContainerRef: Maybe<HTMLDivElement> }) => {
  const pieChartDataSegments = getPieChartDataSegments(data, numberSlices);

  const orderedLegendNames = pieChartDataSegments.chartData.map(({ name }) =>
    toLegendName(name)
  );

  const clickHandler = getClickHandler?.(
    pieChartDataSegments.otherDataSegments
  );

  return (
    <Chart
      chartOptions={getPieChartOptions(
        pieChartDataSegments,
        orderedLegendNames,
        Boolean(clickHandler),
        colors,
        showTotal,
        numberFormatOptions,
        dashboardContainerRef,
        userLocale,
        i18nLocale,
        isAbbreviated
      )}
      clickHandler={clickHandler}
      getResizeOptions={getResizeOptionsCallback(
        pieChartDataSegments,
        showTotal
      )}
      legendNames={orderedLegendNames}
    />
  );
};

type PieChartAriaDescriptionArgs = {
  chartData: Array<{ name: string; value: number }>;
  count: number;
  formattedTotal: string;
  myFormatNumber: (number: number) => string;
  shouldUseDecimalPoint: boolean;
  i18nLocale: I18nLocale;
};

export const getPieChartAriaDescription = ({
  chartData,
  count,
  formattedTotal,
  myFormatNumber,
  shouldUseDecimalPoint,
  i18nLocale,
}: PieChartAriaDescriptionArgs): string => {
  const segments = chartData
    .map(({ name, value }) =>
      t(
        i18nLocale,
        'dashboard:pieChart.ariaSegment',
        {
          name: toLegendName(name),
          count: myFormatNumber(value),
          percent: getLegendPercentage(
            (100 * value) / count,
            shouldUseDecimalPoint
          ),
        },
        '{{name}}: {{percent}} ({{count}})'
      )
    )
    .join('. ');
  return t(
    i18nLocale,
    'dashboard:pieChart.ariaDescription',
    { total: formattedTotal, segments },
    'Pie chart. Total {{total}}. {{segments}}.'
  );
};

export { PieChartWidget };
